import { NODE_HANDLES } from './nodeTypes';

/* ─────────────────────────────────────────────────────────────────
   Per-source-handle edge styling. Maps a sourceHandle id → a
   semantic color so users can read the wires at a glance:
     Open hours / has tag / known       → emerald (positive)
     Closed                              → amber (limit)
     No answer / busy                    → rose (failure path)
     Menu timeout                        → amber
     Tag lacks / contact unknown         → slate (neutral negative)
     Menu DTMF keys                      → sky
     Default `out` (Start, etc.)         → teal
   Each decorated edge also gets a label chip (the handle's human
   label), thicker stroke, and a higher zIndex so it never visually
   "detaches" or hides behind a node during drag.
   ───────────────────────────────────────────────────────────────── */

const HANDLE_COLOR = {
  out: '#2dd4bf',
  open: '#34d399',
  closed: '#fbbf24',
  has: '#34d399',
  lacks: '#94a3b8',
  known: '#34d399',
  unknown: '#94a3b8',
  no_answer: '#fb7185',
  timeout: '#fbbf24',
};

function colorForHandle(handleId) {
  if (!handleId) return '#2dd4bf';
  if (HANDLE_COLOR[handleId]) return HANDLE_COLOR[handleId];
  if (handleId.startsWith('key_')) return '#38bdf8'; // Menu DTMF options
  return '#2dd4bf';
}

function labelForHandle(node, handleId) {
  if (!node) return '';
  const handlesDef = NODE_HANDLES[node.type];
  const handles = typeof handlesDef === 'function'
    ? handlesDef({ data: node.data })
    : (handlesDef || []);
  const h = handles.find((x) => x.id === handleId);
  return h ? h.label : '';
}

/**
 * Decorate edges with color, label, and hover-dim opacity.
 *
 * @param {Array} edges       raw edges from DB / state
 * @param {Array} nodes       raw nodes (used to look up handle labels)
 * @param {Object} options
 *   - hoveredNodeId: when set, edges NOT touching this node fade to 0.25
 *   - selectedNodeId: same as hovered but with stronger emphasis
 *   - showLabels: bool, default true — render the source-handle label chip
 *   - readOnly: bool, default false — skip `interactionWidth` (public replay)
 *   - trafficCounts: { "{source_id}::{handle}": int } — appends call count
 *     to the label chip, e.g. "Open hours · 42 calls"
 */
export function decorateEdges(edges, nodes, options = {}) {
  const { hoveredNodeId, selectedNodeId, showLabels = true, readOnly = false, trafficCounts = null } = options;
  const nodeMap = {};
  (nodes || []).forEach((n) => { nodeMap[n.id] = n; });

  const focus = hoveredNodeId || selectedNodeId || null;

  return (edges || []).map((e) => {
    const sourceNode = nodeMap[e.source];
    const color = colorForHandle(e.sourceHandle);
    let label = showLabels ? labelForHandle(sourceNode, e.sourceHandle) : '';
    if (trafficCounts && label) {
      const key = `${e.source}::${e.sourceHandle || 'out'}`;
      const count = trafficCounts[key] ?? 0;
      label = `${label} · ${count} call${count === 1 ? '' : 's'}`;
    }
    const isFocused = !focus || e.source === focus || e.target === focus;
    const opacity = focus && !isFocused ? 0.22 : 0.95;

    return {
      ...e,
      type: e.type || 'smoothstep',
      pathOptions: { borderRadius: 16 },
      animated: e.animated !== false,
      label: label || undefined,
      labelStyle: {
        fill: color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      },
      labelShowBg: true,
      labelBgStyle: {
        fill: 'var(--vox-label-bg, #0c0d12)',
        fillOpacity: 0.98,
        stroke: color,
        strokeOpacity: 0.35,
      },
      labelBgPadding: [5, 2],
      labelBgBorderRadius: 4,
      style: {
        ...(e.style || {}),
        stroke: color,
        strokeWidth: 2,
        opacity,
      },
      ...(readOnly ? {} : { interactionWidth: 20 }),
      zIndex: 10,
    };
  });
}

/**
 * For a given node, return a Set of output-handle ids that DON'T
 * have an outgoing edge wired up. The canvas surfaces these as
 * yellow ⚠️ chips on the node so the user can spot dead branches
 * before saving.
 */
export function getUnwiredHandleIds(node, edges) {
  if (!node) return new Set();
  const handlesDef = NODE_HANDLES[node.type];
  const handles = typeof handlesDef === 'function'
    ? handlesDef({ data: node.data })
    : (handlesDef || []);
  if (handles.length === 0) return new Set();
  const wired = new Set();
  (edges || []).forEach((e) => {
    if (e.source === node.id && e.sourceHandle) wired.add(e.sourceHandle);
  });
  return new Set(handles.filter((h) => !wired.has(h.id)).map((h) => h.id));
}
